import { DestroyRef, inject, Type } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/ui-dialog-brain';
import { defaults } from 'lodash';

import { HlmDialogOptions, HlmDialogService } from '../index';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace Dialog {
    export type Constructor<Context, Result = unknown> = Type<
        Dialog<Context, Result>
    > & {
        readonly baseOptions: Partial<HlmDialogOptions>;
    };
}

export abstract class Dialog<Context = unknown, Result = NonNullable<unknown>> {
    static readonly baseOptions: Partial<HlmDialogOptions> = {};

    protected readonly ref = inject<BrnDialogRef<Result>>(BrnDialogRef);
    protected readonly context = injectBrnDialogContext<Context>();

    /**
     * Closes the dialog with the provided result.
     * This should be called when the dialog is done processing and has a result to return, e.g. the confirmation of an action.
     *
     * @param result - The result to return from the dialog.
     */
    closeWithResult(result: Result) {
        this.ref.close(result);
    }

    /**
     * Closes the dialog without a result.
     * This should be called when the dialog is canceled or closed without any specific result.
     */
    cancel() {
        this.ref.close();
    }
}

export interface UseDialog {
    /**
     * Opens a dialog component with the specified options and callbacks.
     *
     * @param dialog - The dialog component to open.
     * @param options - Optional configuration options for the dialog.
     * @param resultCallback - Callback function to handle the result when the dialog is closed with a result.
     * @param cancelledCallback - Callback function to handle the case when the dialog is closed without a result (e.g., canceled).
     */
    open: <Context, Result>(
        dialog: Dialog.Constructor<Context, Result>,
        options?: Partial<HlmDialogOptions<Context>>,
        resultCallback?: (result: Result) => void,
        cancelledCallback?: () => void
    ) => BrnDialogRef<Result>;
}

export const useDialog = (): UseDialog => {
    const hlmDialogService = inject(HlmDialogService);
    const destroyRef = inject(DestroyRef);

    return {
        open: <Context, Result>(
            dialog: Dialog.Constructor<Context, Result>,
            options?: Partial<HlmDialogOptions<Context>>,
            resultCallback?: (result: Result) => void,
            cancelledCallback?: () => void
        ) => {
            const dialogRef = hlmDialogService.openComponent(
                dialog,
                defaults({}, options, dialog.baseOptions, {
                    contentClass: 'tw-sm:max-w-[40rem]'
                })
            );

            dialogRef.closed$
                .pipe(takeUntilDestroyed(destroyRef))
                .subscribe((result: Result) => {
                    if (result && resultCallback) {
                        resultCallback(result);
                    } else if (result === undefined && cancelledCallback) {
                        cancelledCallback();
                    }
                });

            return dialogRef;
        }
    };
};
